import { Component } from "@angular/core";
import { ContratoTerceirizadoService } from "compra-lib";
import {
  AssinaturaService, BaseResourceRptPersonalizadoComponent,
  ContratoTerceirizado, EddyAutoComplete, Favorecido, FavorecidoService,
  Setor, SetorService
} from "eddydata-lib";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: 'app-contrato-funcionarios-terceirizados-rpt-dlg',
  templateUrl: './contrato-funcionarios-terceirizados-rpt-dlg.component.html'
})
export class ContratoFuncionariosTerceirizadosRptDlgComponent extends BaseResourceRptPersonalizadoComponent {

  public beneficiario: Favorecido;
  public funcionario: ContratoTerceirizado;
  public cargo: ContratoTerceirizado;
  public local: Setor;

  public dataInicio: Date;
  public dataFinal: Date;

  public beneficiarioAutoComplete: EddyAutoComplete<Favorecido>;
  public funcionarioAutoComplete: EddyAutoComplete<ContratoTerceirizado>;
  public cargoAutoComplete: EddyAutoComplete<ContratoTerceirizado>;
  public localAutoComplete: EddyAutoComplete<Setor>;

  constructor(
    protected assinaturaService: AssinaturaService,
    private favorecidoService: FavorecidoService,
    private contratoTerceirizadoService: ContratoTerceirizadoService,
    private setorService: SetorService
  ) {
    super(assinaturaService);
  }

  protected afterInit(): void {
    this.beneficiarioAutoComplete = new EddyAutoComplete(null, this.favorecidoService,
      'id', ['cpf_cnpj', 'nome'], {
      'contratos.id$not_null': true,
      orderBy: 'nome'
    }, { number: ['cpf_cnpj'], text: ['cpf_cnpj', 'nome'] });

    this.funcionarioAutoComplete = new EddyAutoComplete(null, this.contratoTerceirizadoService,
      'id', ['cpf', 'nome'], { orderBy: 'nome' },
      { number: ['cpf'], text: ['cpf', 'nome'] }
    );

    this.cargoAutoComplete = new EddyAutoComplete(null, this.contratoTerceirizadoService,
      'id', ['cargo'], { orderBy: 'cargo' }, { text: ['cargo'] }
    );

    this.localAutoComplete = new EddyAutoComplete(null, this.setorService,
      'id', ['codigo', 'nome'], { orgao_id: this.login.orgao.id, orderBy: 'codigo' },
      { number: ['codigo'], text: ['codigo', 'nome'] }
    );
  }

  protected modalRelatorio(): string {
    return 'dialogRelatorioContratoFuncionariosTerceirizados';
  }

  protected tituloRelatorio(): string {
    return `Listagem de Funcionários Terceirizados`;
  }

  protected layoutRelatorio(): {} {
    return;
  }

  protected async montarConteudo(): Promise<{}[]> {
    const conteudo = [];

    const dados = await this.buscarDados();
    const gruposContratos = this.funcaoService.agrupar(dados, ['contrato.numero', 'contrato.favorecido.nome']);

    const body = [];
    let count = 0;
    for (const contrato of gruposContratos) {
      body.push([
        {
          text: `Contrato: ${this.funcaoService.mascarar('00000/0000', this.funcaoService.strZero(contrato.grupo['contrato.numero'], 9))} - ${contrato.grupo['contrato.favorecido.nome']}`,
          bold: true, border: [false, false, false, false], colSpan: 6, fontSize: 11, margin: [0, count == 0 ? 0 : 10, 0, 0]
        }, '', '', '', '', ''
      ]);
      body.push([
        {
          text: `Vigência: ${this.funcaoService.converteDataBR(contrato.registros[0]['contrato.data_inicio'])} - ${this.funcaoService.converteDataBR(contrato.registros[0]['contrato.data_termino'])}`,
          border: [false, false, false, false], colSpan: 6, fontSize: 10
        }, '', '', '', '', ''
      ]);

      body.push([
        { text: 'RELAÇÃO DOS SERVIDORES', colSpan: 6, fontSize: 9, bold: true, alignment: 'center' },
        '', '', '', '', ''
      ], [
        { text: 'NOME', fontSize: 9, bold: true },
        { text: 'CPF', fontSize: 9, bold: true },
        { text: 'CARGO/FUNÇÃO', fontSize: 9, bold: true },
        { text: 'LOCAL DE TRABALHO', fontSize: 9, bold: true },
        { text: 'DATA ADMISSÃO', fontSize: 9, bold: true },
        { text: 'DATA DEMISSÃO', fontSize: 9, bold: true }
      ]);

      for (const registro of contrato.registros) {
        body.push([
          { text: registro.nome },
          { text: registro.cpf },
          { text: registro.cargo },
          { text: `${registro['setor.codigo']} - ${registro['setor.nome']}` },
          { text: this.funcaoService.converteDataBR(registro.data_admissao), alignment: 'right' },
          { text: this.funcaoService.converteDataBR(registro.data_demissao), alignment: 'right' }
        ]);
      }

      count++;
    }


    conteudo.push({
      columns: [
        {
          table: {
            widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: body
          }
        }
      ]
    });

    return conteudo;
  }

  private async buscarDados(): Promise<[]> {
    const parametros = {};

    if (this.dataInicio) {
      parametros['data_admissao$ge'] = this.funcaoService.converteDataSQL(this.dataInicio);
    }
    if (this.dataFinal) {
      parametros['data_admissao$le'] = this.funcaoService.converteDataSQL(this.dataFinal);
    }

    if (this.beneficiario) {
      parametros['contrato.favorecido.id'] = this.beneficiario.id;
    }

    if (this.funcionario) {
      parametros['OR'] = `nome=${this.funcionario.nome};!;!;cpf=${this.funcionario.cpf}`;
    }

    if (this.cargo) {
      parametros['cargo'] = this.cargo.cargo;
    }

    if (this.local) {
      parametros['setor.id'] = this.local.id;
    }

    return await new Promise(async (resolve) => {
      this.contratoTerceirizadoService
        .filtrar(1, -1, {
          onlyFields: true,
          fields: [
            'nome', 'cpf', 'cargo', 'data_admissao', 'data_demissao', 'contrato.numero',
            'contrato.data_inicio', 'contrato.data_termino', 'contrato.favorecido.nome',
            'contrato.ano', 'setor.codigo', 'setor.nome'
          ].join(),
          orderBy: 'contrato.ano,contrato.numero,nome',
          ...parametros
        }, true)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(res => {
          resolve(res.content);
        });
    });
  }
}